(() => {
  const SELECTOR =
    '[data-footer-parallax]';

  const INNER_SELECTOR =
    '[data-footer-parallax-inner]';

  const PAGE_WRAPPER_SELECTOR =
    '.page-wrapper';

  const reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  const instances = new Map();


  /* =========================================
     HELPERS
     ========================================= */

  function clamp(
    value,
    min,
    max
  ) {
    return Math.min(
      Math.max(
        value,
        min
      ),
      max
    );
  }

  function getWrappers(
    root = document
  ) {
    const wrappers = [];

    if (
      root instanceof Element &&
      root.matches(SELECTOR)
    ) {
      wrappers.push(root);
    }

    if (root.querySelectorAll) {
      wrappers.push(
        ...root.querySelectorAll(
          SELECTOR
        )
      );
    }

    return wrappers;
  }

  function getViewportHeight() {
    if (
      window.visualViewport &&
      window.visualViewport.height
    ) {
      return window.visualViewport.height;
    }

    return (
      window.innerHeight ||
      document.documentElement.clientHeight
    );
  }

  function setTransform(
    element,
    yPercent
  ) {
    if (window.gsap) {
      gsap.set(
        element,
        {
          yPercent,
        }
      );

      return;
    }

    element.style.transform =
      `translate3d(0, ${yPercent}%, 0)`;
  }

  function clearTransform(element) {
    if (window.gsap) {
      gsap.set(
        element,
        {
          clearProps: 'transform',
        }
      );

      return;
    }

    element.style.transform = '';
  }


  /* =========================================
     CREATE
     ========================================= */

  function createInstance(wrapper) {
    const inner =
      wrapper.querySelector(
        INNER_SELECTOR
      );

    if (!inner) {
      return null;
    }

    const controller =
      new AbortController();

    const { signal } = controller;

    const pageWrapper =
      document.querySelector(
        PAGE_WRAPPER_SELECTOR
      );

    let raf = null;
    let resizeObserver = null;
    let destroyed = false;


    /* =========================================
       POSITION
       ========================================= */

    function update() {
      raf = null;

      if (
        destroyed ||
        !wrapper.isConnected
      ) {
        return;
      }

      if (reducedMotionQuery.matches) {
        setTransform(
          inner,
          0
        );

        return;
      }

      const rect =
        wrapper.getBoundingClientRect();

      const viewportHeight =
        getViewportHeight();

      const wrapperHeight =
        wrapper.offsetHeight ||
        rect.height;

      if (wrapperHeight <= 0) {
        return;
      }

      /*
       * Preserve the established footer reveal:
       *
       * entering viewport: -25%
       * fully revealed:      0%
       */
      const progress =
        clamp(
          (
            viewportHeight -
            rect.top
          ) /
            wrapperHeight,
          0,
          1
        );

      const yPercent =
        -25 *
        (
          1 -
          progress
        );

      setTransform(
        inner,
        yPercent
      );
    }

    function requestUpdate() {
      if (
        destroyed ||
        raf !== null
      ) {
        return;
      }

      raf =
        window.requestAnimationFrame(
          update
        );
    }


    /* =========================================
       SCROLL / RESIZE
       ========================================= */

    window.addEventListener(
      'scroll',
      requestUpdate,
      {
        passive: true,
        signal,
      }
    );

    window.addEventListener(
      'resize',
      requestUpdate,
      {
        passive: true,
        signal,
      }
    );

    if (pageWrapper) {
      pageWrapper.addEventListener(
        'scroll',
        requestUpdate,
        {
          passive: true,
          signal,
        }
      );
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        requestUpdate,
        {
          passive: true,
          signal,
        }
      );

      window.visualViewport.addEventListener(
        'scroll',
        requestUpdate,
        {
          passive: true,
          signal,
        }
      );
    }

    if (
      'ResizeObserver' in window
    ) {
      resizeObserver =
        new ResizeObserver(
          requestUpdate
        );

      resizeObserver.observe(
        wrapper
      );

      resizeObserver.observe(
        inner
      );
    }

    requestUpdate();


    /* =========================================
       INSTANCE
       ========================================= */

    return {
      wrapper,

      requestUpdate,

      destroy() {
        if (destroyed) {
          return;
        }

        destroyed = true;

        controller.abort();

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        if (raf !== null) {
          window.cancelAnimationFrame(
            raf
          );

          raf = null;
        }

        clearTransform(inner);

        instances.delete(
          wrapper
        );
      },
    };
  }


  /* =========================================
     LIFECYCLE
     ========================================= */

  function initFooterParallax(
    root = document
  ) {
    getWrappers(root).forEach(
      (wrapper) => {
        const existing =
          instances.get(wrapper);

        if (existing) {
          existing.destroy();
        }

        const instance =
          createInstance(wrapper);

        if (instance) {
          instances.set(
            wrapper,
            instance
          );
        }
      }
    );
  }

  function destroyFooterParallax(
    root = document
  ) {
    getWrappers(root).forEach(
      (wrapper) => {
        instances
          .get(wrapper)
          ?.destroy();
      }
    );
  }

  function requestAllUpdates() {
    instances.forEach(
      (instance) => {
        instance.requestUpdate();
      }
    );
  }

  function requestAllAfterLayout() {
    window.requestAnimationFrame(
      () => {
        window.requestAnimationFrame(
          requestAllUpdates
        );
      }
    );
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  function init() {
    initFooterParallax(
      document
    );

    /*
     * Preserve the late initial-layout checks used
     * while Shopify, fonts, and media settle.
     */
    window.setTimeout(
      requestAllUpdates,
      250
    );

    window.setTimeout(
      requestAllUpdates,
      800
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true,
      }
    );
  } else {
    init();
  }


  /* =========================================
     SHOPIFY THEME EDITOR
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initFooterParallax(
        event.target
      );

      requestAllAfterLayout();
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyFooterParallax(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:reorder',
    requestAllAfterLayout
  );


  /* =========================================
     BARBA / LENIS LIFECYCLE
     ========================================= */

  window.addEventListener(
    'wambolt:page:enter',
    requestAllAfterLayout
  );

  window.addEventListener(
    'wambolt:lenis:ready',
    requestAllAfterLayout
  );


  /* =========================================
     REDUCED MOTION
     ========================================= */

  function addMediaQueryListener(
    query,
    handler
  ) {
    if (
      typeof query.addEventListener ===
      'function'
    ) {
      query.addEventListener(
        'change',
        handler
      );

      return;
    }

    if (
      typeof query.addListener ===
      'function'
    ) {
      query.addListener(handler);
    }
  }

  addMediaQueryListener(
    reducedMotionQuery,
    requestAllAfterLayout
  );
})();